import { Injectable, Type } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PortalConfirmDialogComponent } from '../portal-ui/wrappers/portal-confirm-dialog/portal-confirm-dialog.component';
import { PortalPromptDialogComponent } from '../portal-ui/wrappers/portal-prompt-dialog/portal-prompt-dialog.component';
import { PortalPromptSelectDialogComponent } from '../portal-ui/wrappers/portal-prompt-select-dialog/portal-prompt-select-dialog.component';
import type {
  PortalConfirmOptions,
  PortalConfirmResult,
  PortalPromptSelectOptions,
  PortalPromptTextOptions,
} from './portal-dialog.types';

export type {
  PortalConfirmOptions,
  PortalConfirmField,
  PortalConfirmResult,
  PortalPromptTextOptions,
  PortalPromptSelectOptions,
  PortalPromptSelectOption,
  PortalDialogConfirmVariant,
  PortalDialogSize,
} from './portal-dialog.types';

const DEFAULT_MODAL_OPTIONS: NgbModalOptions = {
  centered: true,
  backdrop: 'static',
  keyboard: false,
  windowClass: 'portal-dialog-window',
  modalDialogClass: 'portal-dialog-modal-dialog',
};

@Injectable({ providedIn: 'root' })
export class PortalDialogService {
  constructor(private modal: NgbModal) {}

  /**
   * Blocking yes/no confirm. Resolves true on confirm, false on cancel/dismiss.
   */
  async confirm(options: PortalConfirmOptions): Promise<boolean> {
    return (await this.confirmResult(options)).confirmed;
  }

  /**
   * Confirm that can return values from editable fields.
   */
  async confirmResult(options: PortalConfirmOptions): Promise<PortalConfirmResult> {
    const ref = this.modal.open(PortalConfirmDialogComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: options.size === 'lg' ? 'lg' : options.size === 'md' ? 'md' : undefined,
    });
    ref.componentInstance.options = options;
    try {
      const result = await ref.result;
      if (result && typeof result === 'object' && !Array.isArray(result)) {
        return { confirmed: true, values: result as Record<string, string> };
      }
      return { confirmed: true, values: {} };
    } catch {
      return { confirmed: false, values: {} };
    }
  }

  /**
   * Single-field prompt. Resolves the entered string on confirm, null on cancel.
   */
  async promptText(options: PortalPromptTextOptions): Promise<string | null> {
    const ref = this.modal.open(PortalPromptDialogComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: options.size === 'lg' ? 'lg' : options.size === 'md' ? 'md' : undefined,
    });
    ref.componentInstance.options = options;
    try {
      return (await ref.result) as string;
    } catch {
      return null;
    }
  }

  /**
   * Single-select prompt. Resolves the selected value on confirm, null on cancel.
   */
  async promptSelect(options: PortalPromptSelectOptions): Promise<string | null> {
    const ref = this.modal.open(PortalPromptSelectDialogComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: options.size === 'lg' ? 'lg' : options.size === 'md' ? 'md' : undefined,
    });
    ref.componentInstance.options = options;
    try {
      return (await ref.result) as string;
    } catch {
      return null;
    }
  }

  /**
   * Open a page-owned dialog component with dark-chrome NgbModal chrome defaults.
   * Component should typically embed `app-portal-modal-shell` with `[showOverlay]="false"`.
   */
  open<T>(component: Type<T>, options: NgbModalOptions = {}): NgbModalRef {
    return this.modal.open(component, {
      ...DEFAULT_MODAL_OPTIONS,
      ...options,
      windowClass: [DEFAULT_MODAL_OPTIONS.windowClass, options.windowClass]
        .filter(Boolean)
        .join(' '),
    });
  }
}
